// Copyright 2025 Linka Cloud  All rights reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { For } from '@linka-cloud/components/For';
import React, { PropsWithChildren } from 'react';

export type Template<T> =
  | T
  | {
      [K in keyof T]: string;
    };

export type MaybeTemplate<T> = T | Template<T>;

export const withoutQuotes = (s: string) => `###${s}###`;

export const withoutTemplate = <T,>(v: Exclude<T, string> | string, def: Exclude<T, string>): Exclude<T, string> =>
  typeof v === 'string' ? def : v;

export interface GoTemplateProps {
  value: string;
}

export interface GoTemplateData<T> {
  data: T;
}

export const isStaticRendering = typeof window === 'undefined';

export const ifStaticRendering = <T,>(v: T) => (isStaticRendering ? v : null);

export const GoTemplate = ({ value }: GoTemplateProps) =>
  ifStaticRendering(<div data-go-template={value} suppressHydrationWarning />);

export type GoTemplatePropsWithChildren<T> = GoTemplateProps & PropsWithChildren & GoTemplateData<T>;

export const GoBlock = <T,>({ value, children }: GoTemplatePropsWithChildren<T>) =>
  ifStaticRendering(
    <>
      <GoTemplate value={value} />
      {children}
      <GoTemplate value="{{ end }}" />
    </>
  );

export interface GoRangeProps<T> extends GoTemplateProps {
  template: Template<T>;
  each: T[] | readonly T[] | undefined | string;
  children: (item: MaybeTemplate<T>, index: number) => React.ReactNode;
}

export const GoRange = <T,>({ value, each, template, children }: GoRangeProps<T>) => {
  if (isStaticRendering || typeof each === 'string')
    return (
      <>
        <GoTemplate value={`{{ range ${value} }}`} />
        {children(template, 0)}
        <GoTemplate value="{{ end }}" />
      </>
    );
  return <For each={each} children={children} />;
};

export const GoIf = ({ value, data, children }: GoTemplatePropsWithChildren<Record<string, any> | string | number>) => {
  if (isStaticRendering)
    return (
      <>
        <GoTemplate value={`{{ if ${value} }}`} />
        {children}
        <GoTemplate value="{{ end }}" />
      </>
    );
  if (!data) {
    return null;
  }
  if (ifElseReplace(`{{ if ${value} }}true{{ end }}`, data)) {
    return <>{children}</>;
  }
  return null;
};

export interface GoIfElseProps<T, U extends string | number | Record<string, any>> extends GoTemplateProps {
  ifv: T;
  elsev: T;
  mock: U;
}

export const GoIfElse = <T, U extends string | number | Record<string, any>>({
  value,
  ifv,
  elsev,
  mock,
}: GoIfElseProps<T, U>) => {
  if (isStaticRendering)
    return (
      <>
        <GoTemplate value={`{{ if ${value} }}`} />
        {ifv}
        <GoTemplate value="{{ else }}" />
        {elsev}
        <GoTemplate value="{{ end }}" />
      </>
    );
  return ifElseReplace(`{{ if ${value} }}true{{ else }}false{{ end }}`, mock) === 'true' ? ifv : elsev;
};

export const goIf = <T, U extends string | number | Record<string, any>>(cond: string, value: T, mock: U) => {
  const t = `{{ if ${cond} }}${value}{{ end }}`;
  if (isStaticRendering) return t;
  return ifElseReplace(t, mock);
};

export const goIfElse = <T extends string | number | Record<string, any>>(
  cond: string,
  ifv: string,
  elsev: string,
  mock: T
) => {
  const t = `{{ if ${cond} }}${ifv}{{ else }}${elsev}{{ end }}`;
  if (isStaticRendering) return t;
  return ifElseReplace(t, mock);
};

export function ifElseReplace(str: string, variables: Record<string, any> | string | number): string {
  const regex =
    /{{\s*if\s*(?:(?<neg>not)\s+)?(?:(?<op>eq|ne|lt|gt|le|ge)\s+)?(?<cond1>\S+)(?:\s+(?<cond2>\S+))?\s*}}(?<onTrue>[\S\s]*?)(?:{{\s*else\s*}}(?<onFalse>[\S\s]*?))?{{\s*end\s*}}/;
  let result = str;

  const m = regex.exec(result);
  // console.log('regex', m)
  if (!m || !m.groups) {
    // console.log(
    //   "str", str,
    //   "op", op,
    //   "condition", condition,
    //   "condition2", condition2,
    //   "onTrue", onTrue,
    //   "onFalse", onFalse,
    //   "result", result,
    //   "variables", variables,
    // )
    return '';
  }

  const { neg, op, cond1, cond2 } = m.groups;
  let { onTrue, onFalse } = m.groups;

  if (neg) {
    const t = onTrue;
    onTrue = onFalse;
    onFalse = t;
  }

  // console.log(
  //   "str", str,
  //   "neg", neg,
  //   "op", op,
  //   "condition", cond1,
  //   "condition2", cond2,
  //   "onTrue", onTrue,
  //   "onFalse", onFalse,
  //   "result", result,
  //   "variables", variables,
  // )

  switch (op) {
    case 'eq':
      result = evalCondition(cond1, variables) == evalCondition(cond2, variables) ? onTrue : onFalse;
      break;
    case 'ne':
      result = evalCondition(cond1, variables) != evalCondition(cond2, variables) ? onTrue : onFalse;
      break;
    case 'lt':
      result = evalCondition(cond1, variables) < evalCondition(cond2, variables) ? onTrue : onFalse;
      break;
    case 'gt':
      result = evalCondition(cond1, variables) > evalCondition(cond2, variables) ? onTrue : onFalse;
      break;
    case 'le':
      result = evalCondition(cond1, variables) <= evalCondition(cond2, variables) ? onTrue : onFalse;
      break;
    case 'ge':
      result = evalCondition(cond1, variables) >= evalCondition(cond2, variables) ? onTrue : onFalse;
      break;
    case undefined:
    case '':
      result = evalCondition(cond1, variables) ? onTrue : onFalse;
      break;
    default:
      throw new Error(`Functionality not implemented: ${op}`);
  }
  return result;
}

function evalCondition(condition: string, variables: Record<string, any> | string | number) {
  let value: any = condition;
  if (condition.startsWith('.')) {
    value = get(variables, condition.substring(1));
  } else if (condition.startsWith('"') && condition.endsWith('"')) {
    value = condition.substring(1, condition.length - 1);
  }
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  return Boolean(condition);
}

function get(object: Record<string, any> | string | number, path: string | any[]): any {
  if (path === '.') {
    if (typeof object === 'object') {
      throw new Error(`Cannot evaluate '.' with object`);
    }
    return object;
  }
  if (typeof path === 'string') {
    path = path.split('.').filter((key) => key.length);
  }

  return path.reduce((dive, key) => dive?.[key], object);
}
